import { SparklesIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import {
  ENTITY_BADGE_CLASSES,
  ENTITY_TYPE_LABELS,
} from "../constants/entities";
import type { EntityRecommendations } from "../hooks/useSandboxPersonas";
import type { SandboxEntityType } from "../types/entities";

const SECTIONS: Array<{
  key: keyof EntityRecommendations;
  entityType: SandboxEntityType;
}> = [
  { key: "artists", entityType: "artist" },
  { key: "genres", entityType: "genre" },
  { key: "venues", entityType: "venue" },
  { key: "events", entityType: "event" },
];

interface RecommendationResultsProps {
  recommendations: EntityRecommendations | null;
  comparison: string | null;
  isLoadingComparison: boolean;
  onCompare: () => void;
  canCompare: boolean;
}

export function RecommendationResults({
  recommendations,
  comparison,
  isLoadingComparison,
  onCompare,
  canCompare,
}: RecommendationResultsProps) {
  if (!recommendations) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-border">
        <p className="text-sm text-muted-foreground">
          Results appear here after running analysis
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs defaultValue="artists">
        <TabsList className="w-full">
          {SECTIONS.map(({ key, entityType }) => (
            <TabsTrigger key={key} value={key} className="flex-1">
              {ENTITY_TYPE_LABELS[entityType]}s
              <span className="ml-1.5 text-xs opacity-60">
                ({recommendations[key].length})
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {SECTIONS.map(({ key, entityType }) => {
          const items = recommendations[key];
          return (
            <TabsContent key={key} value={key} className="mt-3">
              {items.length === 0 ? (
                <p className="text-xs italic text-muted-foreground">
                  No results
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {items.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                    >
                      {item.photo && (
                        <img
                          src={item.photo}
                          alt=""
                          className="h-8 w-8 shrink-0 rounded object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium leading-tight">
                          {item.name}
                        </p>
                        {item.subtitle && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {item.subtitle}
                          </p>
                        )}
                        {item.evidence && (
                          <p className="mt-0.5 truncate text-xs text-primary/50 italic">
                            {item.evidence}
                          </p>
                        )}
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "shrink-0 font-mono text-xs tabular-nums",
                          ENTITY_BADGE_CLASSES[entityType],
                        )}
                      >
                        {(item.similarity * 100).toFixed(1)}%
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="shrink-0 h-7 px-2 text-xs"
                        aria-label={`Open ${item.name} in new tab`}
                        onClick={() =>
                          window.open(item.url, "_blank", "noopener,noreferrer")
                        }
                      >
                        Open
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      <section className="space-y-3 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Description match</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={onCompare}
            disabled={!canCompare || isLoadingComparison}
          >
            {isLoadingComparison ? (
              <Spinner className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <SparklesIcon className="mr-1.5 h-3.5 w-3.5" />
            )}
            {isLoadingComparison ? "Comparing…" : "Compare"}
          </Button>
        </div>
        {!canCompare && !comparison && (
          <p className="text-xs text-muted-foreground">
            Describe the persona first to enable comparison.
          </p>
        )}
        {comparison && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {comparison}
          </p>
        )}
      </section>
    </div>
  );
}
